import type { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  AIAccess,
  AIActionsButton,
  AIActionsButtonVariant,
  getAIAccess,
  getAIActionMenuItems,
  getAIUnavailableMenuItems,
  getOfferedAIActionBindings,
  invokeAIAction,
} from 'components/Buttons/AIActionsButton';
import { isCopilotBusy } from 'components/Copilot/copilotPanelAvailability';
import { copilotIconProps } from 'globalStyles';
import { AIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

/**
 * The AI actions button as a command bar item, undefined without bindings. The one way to put AI on a
 * command bar.
 *
 * On the bar, `commandBarButtonAs` renders the button and ignores the item's own props. The command bar
 * treats it as a component type, so the renderer stays the same function for the life of the scene and
 * reads the current bindings through a ref; a new renderer would remount the button and close its open
 * menu. In the overflow ("...") the command bar renders a plain menu item from the item's props instead:
 * "AI actions" with the AI badge and the same grouped menu as `subMenuProps`, for one binding as for several.
 * While AI cannot run the item opens the menu that says why.
 */
export const useAIActionsCommandBarItem = (bindings: AIActionBinding[]): ICommandBarItemProps | undefined => {
  const { t } = useTranslation(['copilot']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const bindingsRef = useRef<AIActionBinding[]>(bindings);
  bindingsRef.current = bindings;

  const renderAIActionsButton = useCallback(
    () => <AIActionsButton variant={AIActionsButtonVariant.CommandBar} bindings={bindingsRef.current} />,
    [],
  );

  return useMemo(() => {
    const offered = getOfferedAIActionBindings(bindings, appContext);
    if (offered.length === 0) return undefined;

    const access: AIAccess = getAIAccess(appContext);
    const label: string = t('copilot:AIButton.Label');

    if (access === 'off' || access === 'plan') {
      return {
        key: 'ai',
        text: label,
        iconProps: copilotIconProps,
        subMenuProps: {
          items: getAIUnavailableMenuItems(access, offered, t, appContext, (route) => history.push(route)),
        },
        commandBarButtonAs: renderAIActionsButton,
      };
    }

    const invoke = (binding: AIActionBinding) => invokeAIAction(binding, appContext);

    return {
      key: 'ai',
      text: label,
      iconProps: copilotIconProps,
      subMenuProps: { items: getAIActionMenuItems(offered, t, invoke, isCopilotBusy(appContext)) },
      commandBarButtonAs: renderAIActionsButton,
    };
  }, [appContext, bindings, renderAIActionsButton, t, history]);
};
